import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

export interface FlowGraphData {
  parts: Array<FlowGraphItem>;
  total: FlowGraphItem;
}

export interface FlowGraphItem {
  label: string;
  value: string;
}

@Component({
  selector: 'app-flow-graph',
  templateUrl: './flow-graph.component.html',
  styleUrls: ['./flow-graph.component.scss'],
})
export class FlowGraphComponent implements OnChanges {
  @Input('label') public label: string = '';
  @Input('data') public data: FlowGraphData = null;
  @Input('dataLoaded') public dataLoaded: boolean = false;

  private canvasRef: ElementRef;
  @ViewChild('canvas') set canvas(canvas: ElementRef) {
    if (canvas) {
      this.canvasRef = canvas;
      this.drawDiagram();
    }
  }

  constructor(private translator: TranslateService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.canvasRef) {
      this.drawDiagram();
    }
  }

  private drawDiagram() {
    const ctx = this.canvasRef.nativeElement.getContext('2d');
    const width = this.canvasRef.nativeElement.width;
    const height = this.canvasRef.nativeElement.height;
    ctx.clearRect(0, 0, width, height);

    // Define circle properties
    const bigCircleRadius = 70;
    const smallCircleRadius = 50;
    const centerX = width - bigCircleRadius - 50; // Big circle positioned on the right
    const centerY = height / 2;

    const circleColors = ['#4cb140', '#ec7a08', '#5752d1'];
    const offsetBetweenCircles = 150;
    const centerIndex = Math.floor(this.data.parts.length / 2);
    const smallCircles = this.data.parts.map((part, index) => ({
      x: bigCircleRadius + 50,
      y: centerY + (index - centerIndex) * offsetBetweenCircles,
      color: circleColors[index % circleColors.length],
      label: this.translator.instant(part.label),
      value: part.value,
    }));

    // Draw the big circle
    ctx.fillStyle = '#fff';
    ctx.strokeStyle = '#1e78ba';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(centerX, centerY, bigCircleRadius, 0, 2 * Math.PI);
    ctx.fill();
    ctx.stroke();

    // Draw the small circles and connecting lines
    smallCircles.forEach((circle, index) => {
      // Draw filled small circle
      ctx.fillStyle = '#fff'; // Background color for small circle
      ctx.strokeStyle = circle.color; // Outline color for small circle
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(circle.x, circle.y, smallCircleRadius, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();

      // Draw text inside small circle
      ctx.font = '16px Arial';
      ctx.fillStyle = circle.color; // Text color for small circle
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(circle.value, circle.x, circle.y);

      // Draw text label under small circle
      ctx.fillText(circle.label, circle.x, circle.y + smallCircleRadius + 20);

      // Draw curved line to big circle
      ctx.strokeStyle = circle.color; // Color of connecting line
      ctx.lineWidth = 2; // Thickness of connecting line
      ctx.beginPath();
      const controlPointX = centerX - bigCircleRadius;
      const controlPointY = circle.y;
      ctx.moveTo(circle.x + smallCircleRadius, circle.y);
      let endX, endY;
      if (index === 0) {
        // Connect to top side of the big circle
        endX = centerX - bigCircleRadius * Math.cos(Math.PI / 4);
        endY = centerY - bigCircleRadius * Math.sin(Math.PI / 4);
        ctx.quadraticCurveTo(controlPointX, controlPointY, endX, endY);
      } else if (index === 2) {
        // Connect to bottom side of the big circle
        endX = centerX - bigCircleRadius * Math.cos(Math.PI / 4);
        endY = centerY + bigCircleRadius * Math.sin(Math.PI / 4);
        ctx.quadraticCurveTo(controlPointX, controlPointY, endX, endY);
      } else {
        // Connect to left side of the big circle
        endX = centerX - bigCircleRadius;
        endY = centerY;
        ctx.quadraticCurveTo(controlPointX, controlPointY, endX, endY);
      }
      ctx.stroke();

      // Draw arrowhead
      this.drawArrow(ctx, endX, endY, controlPointX, controlPointY, index, circle.color);
    });

    // Draw text inside big circle
    ctx.font = '16px Arial';
    ctx.fillStyle = '#1e78ba';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(this.data.total.value, centerX, centerY);

    // Draw text label under big circle
    ctx.fillText(this.translator.instant(this.data.total.label), centerX, centerY + bigCircleRadius + 20);
  }

  private drawArrow(ctx: CanvasRenderingContext2D, toX: number, toY: number, fromX: number, fromY: number, index: number, color: string) {
    const headlen = 15; // length of head in pixels

    const dx = toX - fromX;
    const dy = toY - fromY;
    const angle = Math.atan2(dy, dx);

    ctx.strokeStyle = color; // Color of arrow
    ctx.lineWidth = 2; // Thickness of arrow

    ctx.beginPath();
    ctx.moveTo(toX, toY);
    ctx.lineTo(toX - headlen * Math.cos(angle - Math.PI / 6), toY - headlen * Math.sin(angle - Math.PI / 6));
    ctx.moveTo(toX, toY);
    ctx.lineTo(toX - headlen * Math.cos(angle + Math.PI / 6), toY - headlen * Math.sin(angle + Math.PI / 6));
    ctx.stroke();
  }
}
